import type { Account, AlpacaCredentialSummary, CredentialListing, CredentialSummary, PolygonCredentialSummary } from '@fleece/models';
import { CircleCheck, CircleX, Eye, PlugZap, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AlpacaDialog, ConfirmDialog, CredentialKeysDialog, PolygonDialog } from '@/components/settings/credential-dialogs';
import { RowActions, Rows } from '@/components/settings/table-parts';
import { Badge, Button, PageHeader, Spinner, StatusDot, Switch, TabLinks, Table, TableCard, Td, Th } from '@/components/ui';
import { useAccounts } from '@/hooks/use-ledger';
import { useCredentials, useDeleteCredential, useTestCredential, useUpdateCredential } from '@/hooks/use-settings';
import { useCredentialTab } from '@/hooks/use-route';
import { CREDENTIAL_TABS } from '@/lib/route';
import { alpacaToggleRequest, checkDisplay, deletionWarning, feedDisplay, groupCredentials, keyStatus, type CheckDisplay } from '@/lib/settings';
import { cn } from '@/lib/utils';

type Dialog =
  | { readonly kind: 'alpaca'; readonly editing?: AlpacaCredentialSummary }
  | { readonly kind: 'polygon'; readonly editing?: PolygonCredentialSummary }
  | { readonly kind: 'keys'; readonly credential: CredentialSummary }
  | { readonly kind: 'delete'; readonly credential: CredentialSummary };

type AlpacaListing = CredentialListing & { readonly credential: AlpacaCredentialSummary };

/** The time, ticking twice a minute, so "checked 3 min ago" does not stand still while the page is open. */
function useNow(): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/**
 * The keys Fleece holds, a tab per provider. Every key is checked with its provider before
 * it is saved, and can be tested again from its row, which records whether the provider
 * still takes it. The keys themselves are fetched only when asked to show them.
 */
export function CredentialsPage() {
  const tab = useCredentialTab();
  const credentials = useCredentials();
  const accounts = useAccounts();
  const now = useNow();
  const [dialog, setDialog] = useState<Dialog | undefined>();
  const close = (): void => setDialog(undefined);
  const groups = groupCredentials(credentials.data?.credentials ?? []);
  const loaded = credentials.data !== undefined;

  return (
    <div className="mx-auto flex max-w-[1100px] flex-col gap-3 px-2 py-3 pb-6 sm:px-3">
      <PageHeader title="Credentials" description="Keys are checked with their provider before they are saved. Test them again at any time, or show them to copy." />

      <TabLinks
        label="Providers"
        current={tab}
        tabs={CREDENTIAL_TABS.map((entry) => ({ ...entry, count: loaded ? (entry.tab === 'alpaca' ? groups.alpaca.length : groups.polygon.length) : undefined }))}
      />

      {tab === 'alpaca' ? (
        <TableCard
          title="Alpaca accounts"
          action={
            <Button variant="primary" onClick={() => setDialog({ kind: 'alpaca' })}>
              <Plus className="size-3.5" /> Add account
            </Button>
          }
        >
          <Rows query={credentials} empty="No Alpaca account yet. Add one to follow its orders and to read market data." count={groups.alpaca.length}>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th className="hidden md:table-cell">Account</Th>
                  <Th className="hidden md:table-cell">Key status</Th>
                  <Th>Order feed</Th>
                  <Th className="hidden text-center md:table-cell">Follow orders</Th>
                  <Th className="hidden text-center md:table-cell">Market data</Th>
                  <Th>
                    <span className="sr-only">Actions</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {groups.alpaca.map((listing) => (
                  <AlpacaRow
                    key={listing.credential.credentialId}
                    listing={listing}
                    defaultAccount={accounts.data?.accounts.find((account) => account.accountId === listing.credential.defaultAccountId)}
                    now={now}
                    onShowKeys={() => setDialog({ kind: 'keys', credential: listing.credential })}
                    onEdit={() => setDialog({ kind: 'alpaca', editing: listing.credential })}
                    onDelete={() => setDialog({ kind: 'delete', credential: listing.credential })}
                  />
                ))}
              </tbody>
            </Table>
          </Rows>
        </TableCard>
      ) : (
        <TableCard
          title="Polygon keys"
          action={
            <Button variant="primary" onClick={() => setDialog({ kind: 'polygon' })}>
              <Plus className="size-3.5" /> Add key
            </Button>
          }
        >
          <Rows query={credentials} empty="No Polygon key. The dividend job needs one to record what each account is owed." count={groups.polygon.length}>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Key status</Th>
                  <Th>
                    <span className="sr-only">Actions</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {groups.polygon.map(({ credential }) => (
                  <PolygonRow
                    key={credential.credentialId}
                    credential={credential}
                    now={now}
                    onShowKeys={() => setDialog({ kind: 'keys', credential })}
                    onEdit={() => setDialog({ kind: 'polygon', editing: credential })}
                    onDelete={() => setDialog({ kind: 'delete', credential })}
                  />
                ))}
              </tbody>
            </Table>
          </Rows>
        </TableCard>
      )}

      <AlpacaDialog open={dialog?.kind === 'alpaca'} editing={dialog?.kind === 'alpaca' ? dialog.editing : undefined} onClose={close} />
      <PolygonDialog open={dialog?.kind === 'polygon'} editing={dialog?.kind === 'polygon' ? dialog.editing : undefined} onClose={close} />
      <CredentialKeysDialog credential={dialog?.kind === 'keys' ? dialog.credential : undefined} onClose={close} />
      <DeleteCredentialDialog credential={dialog?.kind === 'delete' ? dialog.credential : undefined} onClose={close} />
    </div>
  );
}

interface RowProps {
  readonly now: number;
  readonly onShowKeys: () => void;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

/** How long a row shows a Test's answer before it goes back to the status alone. */
const ANSWER_SHOWN_MS = 8_000;

interface RowTest {
  readonly pending: boolean;
  /** Why the provider could not be asked; an answer about the keys is not an error. */
  readonly error?: string;
  /** The last Test's answer, for a few seconds after it came. */
  readonly answer?: CheckDisplay;
  run(): void;
}

/**
 * A row's Test. Its answer is recorded and the list read again, but keys that were valid and
 * still are read the same afterwards, so the answer itself shows for a few seconds as well.
 */
function useRowTest(credential: CredentialSummary): RowTest {
  const test = useTestCredential();
  const [answer, setAnswer] = useState<CheckDisplay>();

  useEffect(() => {
    if (answer === undefined) {
      return;
    }
    const timer = setTimeout(() => setAnswer(undefined), ANSWER_SHOWN_MS);
    return () => clearTimeout(timer);
  }, [answer]);

  return {
    pending: test.isPending,
    error: test.error?.message,
    answer,
    run() {
      setAnswer(undefined);
      test.mutate(credential.credentialId, { onSuccess: (response) => setAnswer(checkDisplay(response.check)) });
    },
  };
}

function AnswerIcon({ answer }: { answer: CheckDisplay }) {
  return answer.tone === 'positive' ? <CircleCheck className="size-3.5 text-positive" /> : <CircleX className="size-3.5 text-negative" />;
}

/** Test, show the keys, edit, delete. */
function CredentialActions({
  credential,
  test,
  provider,
  onShowKeys,
  onEdit,
  onDelete,
}: { credential: CredentialSummary; test: RowTest; provider: string } & Omit<RowProps, 'now'>) {
  return (
    <RowActions name={credential.name} onEdit={onEdit} onDelete={onDelete}>
      <Button
        variant="ghost"
        onClick={test.run}
        disabled={test.pending}
        aria-label={`Test ${credential.name}'s keys with ${provider}`}
        title={test.answer?.message ?? `Test the keys with ${provider}`}
      >
        {test.pending ? <Spinner /> : test.answer !== undefined ? <AnswerIcon answer={test.answer} /> : <PlugZap className="size-3.5" />}
      </Button>
      <Button variant="ghost" onClick={onShowKeys} aria-label={`Show ${credential.name}'s keys`} title="Show the keys">
        <Eye className="size-3.5" />
      </Button>
    </RowActions>
  );
}

/** Whether the provider takes the keys, as it last said, and when — or, just after a Test, what it said. */
function KeyStatus({ credential, answer, now }: { credential: CredentialSummary; answer?: CheckDisplay; now: number }) {
  const status = keyStatus(credential, now);
  return (
    <div className="flex max-w-[16rem] flex-col gap-0.5">
      <StatusDot tone={status.tone} label={status.label} />
      {answer !== undefined ? (
        <TestAnswer answer={answer} />
      ) : (
        status.detail !== undefined && <span className="text-[11px] leading-snug break-words text-muted">{status.detail}</span>
      )}
    </div>
  );
}

/** A Test's answer in words, beside the status it changed. */
function TestAnswer({ answer, className }: { answer: CheckDisplay; className?: string }) {
  return (
    <span role="status" className={cn('flex items-start gap-1 text-[11px] leading-snug break-words', answer.tone === 'positive' ? 'text-positive' : 'text-negative', className)}>
      <span className="mt-px shrink-0">
        <AnswerIcon answer={answer} />
      </span>
      {answer.message}
    </span>
  );
}

function PolygonRow({ credential, now, ...actions }: { credential: PolygonCredentialSummary } & RowProps) {
  const test = useRowTest(credential);
  return (
    <tr>
      <Td>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">{credential.name}</span>
          {test.error !== undefined && <span className="text-[11px] text-negative">{test.error}</span>}
        </div>
      </Td>
      <Td>
        <KeyStatus credential={credential} answer={test.answer} now={now} />
      </Td>
      <Td>
        <CredentialActions credential={credential} test={test} provider="Polygon" {...actions} />
      </Td>
    </tr>
  );
}

/** Where the account's unclaimed orders go: the account's name once the accounts are read, its id until then. */
function DefaultAccount({ credential, account }: { credential: AlpacaCredentialSummary; account?: Account }) {
  return (
    <span className="text-[11px] whitespace-nowrap text-muted" title="Where orders nobody claims are booked. Change it with Edit.">
      Unclaimed to <span className="text-ink-2">{account?.name ?? credential.defaultAccountId}</span>
    </span>
  );
}

function AlpacaRow({ listing, defaultAccount, now, ...actions }: { listing: AlpacaListing; defaultAccount?: Account } & RowProps) {
  const { credential } = listing;
  const update = useUpdateCredential();
  const test = useRowTest(credential);
  const feed = feedDisplay(listing.feed);
  const toggle = (change: { supportData?: boolean; trackOrders?: boolean }): void => update.mutate(alpacaToggleRequest(credential, change));

  return (
    <tr>
      <Td className="min-w-[10.5rem]">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
            {credential.name}
            {credential.live ? <Badge tone="negative">Live</Badge> : <Badge>Paper</Badge>}
            {credential.invalid !== undefined && <Badge tone="negative">Invalid keys</Badge>}
          </span>
          {/* On a narrow screen the account folds in here, rather than widening the table. */}
          <span className="font-mono text-[11px] whitespace-nowrap text-muted md:hidden">{credential.accountId}</span>
          <span className="md:hidden">
            <DefaultAccount credential={credential} account={defaultAccount} />
          </span>
          {update.error !== null && <span className="text-[11px] text-negative">{update.error.message}</span>}
          {test.error !== undefined && <span className="text-[11px] text-negative">{test.error}</span>}
          {/* The Key status column is hidden here, so a Test's answer would show only as the button's icon. */}
          {test.answer !== undefined && <TestAnswer answer={test.answer} className="md:hidden" />}
        </div>
      </Td>
      <Td className="hidden md:table-cell">
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-ink-2">{credential.accountId}</span>
          <DefaultAccount credential={credential} account={defaultAccount} />
        </div>
      </Td>
      <Td className="hidden md:table-cell">
        <KeyStatus credential={credential} answer={test.answer} now={now} />
      </Td>
      <Td className="max-w-[18rem]">
        <div className="flex flex-col gap-0.5">
          <StatusDot tone={feed.tone} label={feed.label} pulse={listing.feed?.state === 'connected'} />
          {feed.detail !== undefined && <span className="text-[11px] leading-snug break-words text-muted">{feed.detail}</span>}
        </div>
      </Td>
      <Td className="hidden text-center md:table-cell">
        <Switch
          hideLabel
          checked={credential.trackOrders}
          disabled={update.isPending}
          onChange={(trackOrders) => toggle({ trackOrders })}
          label={`Follow ${credential.name}'s orders`}
        />
      </Td>
      <Td className="hidden text-center md:table-cell">
        <Switch
          hideLabel
          checked={credential.supportData}
          disabled={update.isPending}
          onChange={(supportData) => toggle({ supportData })}
          label={`Use ${credential.name} for market data`}
        />
      </Td>
      <Td>
        <CredentialActions credential={credential} test={test} provider="Alpaca" {...actions} />
      </Td>
    </tr>
  );
}

function DeleteCredentialDialog({ credential, onClose }: { credential?: CredentialSummary; onClose: () => void }) {
  const remove = useDeleteCredential();
  const warning = credential === undefined ? undefined : deletionWarning(credential);
  return (
    <ConfirmDialog
      open={credential !== undefined}
      title={credential === undefined ? 'Delete' : `Delete ${credential.name}?`}
      message={warning?.message}
      serious={warning?.force}
      confirmLabel={warning?.force === true ? 'Delete live credential' : 'Delete'}
      busy={remove.isPending}
      error={remove.error?.message}
      onClose={() => {
        remove.reset();
        onClose();
      }}
      onConfirm={() => {
        if (credential !== undefined) {
          // This dialog is the confirmation the service asks a live credential's deletion for.
          remove.mutate({ credentialId: credential.credentialId, force: warning?.force }, { onSuccess: onClose });
        }
      }}
    />
  );
}
